import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { AlarmClock } from 'lucide-react';
function getInitialTarget(): number {
  if (typeof window === 'undefined') return Date.now() + 24 * 60 * 60 * 1000;
  const storedTarget = window.localStorage.getItem('aerial_silks_countdown_target');
  if (storedTarget) {
    const parsedTarget = Number.parseInt(storedTarget, 10);
    if (!Number.isNaN(parsedTarget) && parsedTarget > Date.now()) return parsedTarget;
  }
  const target = Date.now() + 24 * 60 * 60 * 1000;
  try {
    window.localStorage.setItem('aerial_silks_countdown_target', target.toString());
  } catch {}
  return target;
}
function pad(value: number) {
  return value.toString().padStart(2, '0');
}
export function CountdownTimer() {
  const [target, setTarget] = useState<number | null>(null);
  const [remaining, setRemaining] = useState(24 * 60 * 60 * 1000);
  useEffect(() => {
    setTarget(getInitialTarget());
  }, []);
  useEffect(() => {
    if (!target) return;
    const tick = () => {
      const difference = target - Date.now();
      if (difference <= 0) {
        const nextTarget = Date.now() + 24 * 60 * 60 * 1000;
        try {
          window.localStorage.setItem('aerial_silks_countdown_target', nextTarget.toString());
        } catch {}
        setTarget(nextTarget);
        setRemaining(24 * 60 * 60 * 1000);
        return;
      }
      setRemaining(difference);
    };
    tick();
    const interval = window.setInterval(tick, 1000);
    return () => window.clearInterval(interval);
  }, [target]);
  const hours = Math.floor(remaining / (1000 * 60 * 60));
  const minutes = Math.floor(remaining / (1000 * 60) % 60);
  const seconds = Math.floor(remaining / 1000 % 60);
  const elapsed = 24 * 60 * 60 * 1000 - remaining;
  const progress = Math.min(90, 65 + elapsed / (24 * 60 * 60 * 1000) * 25);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} className="mt-10 max-w-3xl mx-auto bg-gradient-to-b from-[#2a0808] to-[#1a0404] border border-red-500/40 rounded-2xl p-6 md:p-8 shadow-[0_0_30px_rgba(239,68,68,0.15)]" data-id="element-41" data-mp="2dp8wm6homvvn"><div className="text-center" data-id="element-42" data-mp="2nd4sr8qd2yfl">
      <div className="flex items-center justify-center gap-2 text-red-400 font-bold tracking-wider uppercase text-sm md:text-base mb-6" data-id="element-43" data-mp="2nexwog9lb4jw"><AlarmClock size={20} className="animate-pulse" data-id="element-44" data-mp="2je5mnksw8vmk" /><span data-id="element-45" data-mp="2edriweybs7uc">The launch price ends in:</span></div>
      <div className="flex items-center justify-center gap-3 md:gap-5 mb-5" data-id="element-46" data-mp="2efax800f88th"><TimeBox value={pad(hours)} label="hrs" data-id="element-47" data-mp="29fg6bwgnbj8f" /><span className="text-3xl md:text-5xl font-bold text-red-400" data-id="element-48" data-mp="24n560shztvfc">:</span><TimeBox value={pad(minutes)} label="min" data-id="element-49" data-mp="24lu2mtsgmpor" /><span className="text-3xl md:text-5xl font-bold text-red-400" data-id="element-50" data-mp="1znsis6n7mmc8">:</span><TimeBox value={pad(seconds)} label="sec" data-id="element-51" data-mp="1zlzeuz3zeg7x" /></div>
      <p className="text-xs md:text-sm font-bold tracking-wider uppercase text-gray-200 mb-8" data-id="element-52" data-mp="1vni507t6ermt">After this, the price goes up and it won&apos;t come back down.</p>
      <div className="space-y-2" data-id="element-53" data-mp="1loaq3g6smoxl"><div className="flex justify-between text-[11px] md:text-xs font-bold uppercase tracking-wider" data-id="element-54" data-mp="1lo0yxpa0ibqb"><span className="text-red-300" data-id="element-55" data-mp="16rwagoay0dq">Launch price spots remaining</span><span className="text-red-400" data-id="element-56" data-mp="18me3m4a1cdl">Only a few left</span></div><div className="w-full h-2.5 bg-red-950/60 rounded-full overflow-hidden border border-red-500/30" data-id="element-57" data-mp="641qsaqjt28f"><motion.div initial={{
            width: 0
          }} whileInView={{
            width: `${progress}%`
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.5,
            ease: 'easeOut'
          }} className="h-full bg-gradient-to-r from-red-600 to-red-400 rounded-full shadow-[0_0_10px_rgba(239,68,68,0.6)]" data-id="element-58" data-mp="b5r45x18trzo" /></div><p className="text-right text-[11px] md:text-xs text-red-300 font-medium" data-id="element-59" data-mp="3jjznsflsf8fp">{Math.round(progress)}% of spots filled</p></div>
    </div></motion.div>;
}
function TimeBox({
  value,
  label
}: {
  value: string;
  label: string;
}) {
  return <div className="bg-black border-2 border-red-500/40 rounded-xl px-4 md:px-6 py-3 md:py-4 min-w-[80px] md:min-w-[110px] shadow-[inset_0_0_15px_rgba(239,68,68,0.1)]" data-id="element-60" data-mp="3sdcq285qrmeg"><div className="flex items-baseline justify-center gap-1" data-id="element-61" data-mp="2zw0bh0bkrt86"><span className="text-4xl md:text-5xl font-bold text-white tabular-nums" data-id="element-62" data-mp="2zuflzp44b0h6">{value}</span><span className="text-xs md:text-sm font-medium text-red-300" data-id="element-63" data-mp="2gs59fjoyonpo">{label}</span></div></div>;
}